import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";

// Components by MUI
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import functionsUser from "../services/user.service";
import PrepaidPaymentInterface from "../interfaces/PrepaidPaymentInterface";
import functionsPayment from "../services/payment.service";
import moment from "moment";
import AsaasPaymentStatusUtils from "../utils/AsaasPaymentStatusUtils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGetPayments } from "../requests/get-payments";

const Payment: React.FC = () => {
  // const appContext = useContext(AppContext);
  const { data } = useGetPayments();
  const history = useNavigate();

  const [isLoadingPrepaidBalance, setIsLoadingPrepaidBalance] =
    useState<boolean>(true);
  const [prepaidBalance, setPrepaidBalance] = useState<number>();

  const [isLoadingPrepaidPayments, setIsLoadingPrepaidPayments] =
    useState<boolean>(true);
  const [prepaidPayments, setPrepaidPayments] =
    useState<PrepaidPaymentInterface[]>();

  const allPayments = useMemo(() => {
    const arr = (data?.rows || [])
      .map((payment) => {
        return {
          ...payment,
          provider: "DELBANK",
          date: payment.createdAt,
          value: payment.amount,
          status: payment.status,
          invoiceUrl: "",
          isValueAddedToBalance: false,
          __v: 0,
        };
      })
      .concat(prepaidPayments || []);

    return arr;
  }, [data, prepaidPayments]);

  const fetchPrepaidBalance = useCallback(() => {
    setIsLoadingPrepaidBalance(true);
    setTimeout(() => {
      functionsUser
        .getUserPrepaidBalance()
        .then((response) => {
          setPrepaidBalance(response.data.prepaidBalance);
        })
        .catch((error) => {
          if (JSON.stringify(error).includes("401")) {
            AuthService.Logout();
          }
        });
      setIsLoadingPrepaidBalance(false);
    }, 1000);
  }, []);

  const fetchPrepaidPayments = useCallback(() => {
    // setIsLoadingPrepaidPayments(true);
    setTimeout(() => {
      functionsPayment
        .getPrepaidPayments()
        .then((response) => {
          setPrepaidPayments(response.data.prepaidPayments);
        })
        .catch((error) => {
          if (JSON.stringify(error).includes("401")) {
            AuthService.Logout();
          }
        });
      setIsLoadingPrepaidPayments(false);
    }, 1000);
  }, []);

  useEffect(() => {
    fetchPrepaidBalance();
  }, [fetchPrepaidBalance]);

  useEffect(() => {
    fetchPrepaidPayments();
  }, [fetchPrepaidPayments]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchPrepaidBalance();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchPrepaidBalance]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchPrepaidPayments();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchPrepaidPayments]);

  return (
    <>
      <Box m={2}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/">
            Página inicial
          </Link>
          <Typography color="text.primary" aria-current="page">
            Pagamento
          </Typography>
        </Breadcrumbs>
      </Box>
      <Container>
        <Grid container spacing={2} mt={2} mb={10}>
          <Grid item lg={12} md={12} xs={12}>
            <Typography variant="h4">Pagamento</Typography>
            <Typography variant="subtitle1">
              Faça a gerência do seu saldo e método de pagamento do Leve Aí.
            </Typography>
          </Grid>
          <Grid item lg={12} md={12} xs={12}>
            <Card
              style={{
                backgroundColor: "#FFF",
                borderRadius: 16,
                padding: 0,
                boxShadow:
                  "rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px",
              }}
            >
              <CardHeader title="Saldo Leve Aí" />
              <CardContent>
                <Grid container spacing={4}>
                  <Grid item lg={8} md={8} xs={12}>
                    <Grid container spacing={4}>
                      <Grid item lg={4} md={4} xs={6}>
                        Saldo atual
                      </Grid>
                      <Grid item lg={8} md={8} xs={6} textAlign={"right"}>
                        {isLoadingPrepaidBalance ||
                        prepaidBalance === null ||
                        prepaidBalance === undefined ? (
                          <>
                            <CircularProgress size={17} />
                          </>
                        ) : (
                          <>
                            {prepaidBalance.toLocaleString("pt-br", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={4} md={4} xs={12} textAlign={"right"}>
                    <Button
                      onClick={() => {
                        history("/payment/new");
                      }}
                    >
                      Adicionar saldo
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item lg={12} md={12} xs={12}>
            <Card
              style={{
                backgroundColor: "#FFF",
                borderRadius: 16,
                padding: 0,
                boxShadow:
                  "rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px",
              }}
            >
              <CardHeader title="Histórico de pagamentos" />
              <CardContent>
                {isLoadingPrepaidPayments || !allPayments ? (
                  <>
                    <Box textAlign={"center"}>
                      <CircularProgress size={70} />
                    </Box>
                  </>
                ) : (
                  <>
                    {!!allPayments ? (
                      <>
                        {allPayments ? (
                          <>
                            <Box
                              sx={{
                                display: {
                                  xs: "block",
                                  md: "none",
                                  lg: "none",
                                },
                              }}
                            >
                              <List dense>
                                {allPayments.map((payment) => (
                                  <ListItem
                                    key={payment._id}
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        href={payment.invoiceUrl}
                                        target={"_blank"}
                                      >
                                        <ArrowForwardIcon />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemText
                                      primary={`${moment(payment.date).format(
                                        "DD/MM/YY"
                                      )} - ${payment.value.toLocaleString(
                                        "pt-br",
                                        { style: "currency", currency: "BRL" }
                                      )}`}
                                      secondary={
                                        <Chip
                                          variant={"outlined"}
                                          size={"small"}
                                          label={
                                            AsaasPaymentStatusUtils[
                                              payment.status
                                            ].text
                                          }
                                          style={{
                                            backgroundColor:
                                              AsaasPaymentStatusUtils[
                                                payment.status
                                              ].backgroundColor,
                                            color:
                                              AsaasPaymentStatusUtils[
                                                payment.status
                                              ].textColor,
                                            borderWidth: 0,
                                          }}
                                        />
                                      }
                                    />
                                    {payment.fiscalInvoiceStatus ===
                                      "autorizado" && (
                                      <Button
                                        target={"_blank"}
                                        href={payment?.fiscalInvoiceUrl ?? ""}
                                      >
                                        Ver NF
                                      </Button>
                                    )}
                                  </ListItem>
                                ))}
                              </List>
                            </Box>

                            <Box
                              sx={{
                                display: {
                                  xs: "none",
                                  md: "block",
                                  lg: "block",
                                },
                              }}
                            >
                              <TableContainer>
                                <Table
                                  sx={{ minWidth: 650 }}
                                  size="small"
                                  aria-label="a dense table"
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Data</TableCell>
                                      <TableCell align="right">Valor</TableCell>
                                      <TableCell align="right">
                                        Provedor
                                      </TableCell>
                                      <TableCell align="right">URL</TableCell>
                                      <TableCell align="center">
                                        Status
                                      </TableCell>
                                      <TableCell align="center">
                                        Nota Fiscal
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {allPayments.map((payment) => (
                                      <TableRow
                                        key={payment._id}
                                        sx={{
                                          "&:last-child td, &:last-child th": {
                                            border: 0,
                                          },
                                        }}
                                      >
                                        <TableCell component="th" scope="row">
                                          {moment(payment.date).format(
                                            "DD/MM/YYYY - HH:mm"
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          {payment.value.toLocaleString(
                                            "pt-br",
                                            {
                                              style: "currency",
                                              currency: "BRL",
                                            }
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          {payment.provider}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Link
                                            href={payment.invoiceUrl}
                                            target={"_blank"}
                                          >
                                            {payment.invoiceUrl}
                                          </Link>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Chip
                                            variant={"outlined"}
                                            size={"small"}
                                            label={
                                              AsaasPaymentStatusUtils[
                                                payment.status
                                              ].text
                                            }
                                            style={{
                                              backgroundColor:
                                                AsaasPaymentStatusUtils[
                                                  payment.status
                                                ].backgroundColor,
                                              color:
                                                AsaasPaymentStatusUtils[
                                                  payment.status
                                                ].textColor,
                                              borderWidth: 0,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell align={"center"}>
                                          {payment.fiscalInvoiceStatus ===
                                            "processando_autorizacao" && (
                                            <p>Em processamento</p>
                                          )}
                                          {payment.fiscalInvoiceStatus ===
                                            "cancelado" && <p>Cancelada</p>}
                                          {payment.fiscalInvoiceStatus ===
                                            "erro_autorizacao" && <p>Erro</p>}
                                          {payment.fiscalInvoiceStatus ===
                                            "autorizado" && (
                                            <Button
                                              target={"_blank"}
                                              href={
                                                payment?.fiscalInvoiceUrl ?? ""
                                              }
                                            >
                                              Ver NF
                                            </Button>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </>
                        ) : (
                          <>
                            <p>Nenhum histórico de pagamento disponível.</p>
                          </>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Payment;
